"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { Meal } from "@/types/meal";

export interface CartItem {
  meal: Meal;
  quantity: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (meal: Meal, quantity: number) => void;
  removeFromCart: (mealId: string) => void;
  updateQuantity: (mealId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  getCartArtisanId: () => string | null;
  canAddMealToCart: (mealArtisanId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("drizl-cart");
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setCart(parsed);
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save cart to localStorage whenever it changes (only after initial load)
  useEffect(() => {
    if (!isLoaded) return;
    localStorage.setItem("drizl-cart", JSON.stringify(cart));
  }, [cart, isLoaded]);

  const addToCart = (meal: Meal, quantity: number) => {
    setCart((prevCart) => {
      // Check if meal already exists in cart
      const existingItemIndex = prevCart.findIndex(
        (item) => item.meal.id === meal.id,
      );

      if (existingItemIndex > -1) {
        // Update quantity of existing item
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        return newCart;
      } else {
        // Add new item to cart
        const newCart = [...prevCart, { meal, quantity }];
        return newCart;
      }
    });
  };

  const removeFromCart = (mealId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.meal.id !== mealId));
  };

  const updateQuantity = (mealId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(mealId);
      return;
    }

    setCart((prevCart) => {
      const newCart = [...prevCart];
      const itemIndex = newCart.findIndex((item) => item.meal.id === mealId);
      if (itemIndex > -1) {
        newCart[itemIndex].quantity = quantity;
      }
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce(
      (total, item) => total + item.meal.price * item.quantity,
      0,
    );
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getCartArtisanId = (): string | null => {
    if (cart.length === 0) return null;
    return cart[0].meal.uid || null;
  };

  const canAddMealToCart = (mealArtisanId: string): boolean => {
    if (cart.length === 0) return true; // Empty cart, can add any meal
    const cartArtisanId = getCartArtisanId();
    return cartArtisanId === mealArtisanId;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemCount,
        getCartArtisanId,
        canAddMealToCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
